import {AfterViewInit, Component, ElementRef, EventEmitter, Input, Output, ViewChild} from '@angular/core';
import {CommonModule} from "@angular/common";

@Component({
  selector: 'hero-slider',
  templateUrl: './hero-slider.component.html',
  styleUrl: './hero-slider.component.scss',
  standalone: true,
  imports: [
    CommonModule
  ],
})
export class HeroSliderComponent implements AfterViewInit {

  @Input() min: number = 0;
  @Input() max: number = 100;
  @Input() step: number = 1;
  @Input() values: number[] = [50]; // Dynamic array of thumb values
  @Input() thumbImages: string[] = []; // Array of image URLs for thumbs
  @Input() readOnly: boolean|boolean[] = false; // Read-only mode as a boolean or as array of booleans for each thumb
  @Input() hideLinesBelow: boolean = false; // Hide lines below track
  @Input() hideLinesAbove: boolean = false; // Hide lines above track
  @Input() stepColors : string[] = [
    '#ff4d4f', // Red
    '#40c4ff', // Light Blue
    '#4caf50', // Green
    '#ffca28', // Yellow
    '#ab47bc', // Purple
    '#ff7043', // Orange
    '#26a69a', // Teal
    '#ec407a'  // Pink
  ];
  @Output() valuesChange = new EventEmitter<number[]>();
  @Output() changed = new EventEmitter<number[]>();
  @ViewChild('track') track!: ElementRef<HTMLDivElement>;

  private isDragging: number | null = null;
  private readonly OFFSET_PX: number = 10; // Base offset for overlapping thumbs

  ngAfterViewInit() {
    this.updateTrackGradient();
  }

  /**
   * Generates an array of step objects, each containing value, color, and percentage properties, based on the range, step size, and color configuration.
   *
   * @return {Array<{ value: number, color: string, percentage: number }>} An array of step objects where each step includes:
   * - value: The numeric value of the step.
   * - color: The color associated with the step.
   * - percentage: The percentage representation of the step within the range.
   */
  getSteps(): { value: number, color: string, percentage: number }[] {
    const steps: { value: number, color: string, percentage: number }[] = [];
    const range = this.max - this.min;
    const stepCount = Math.floor(range / this.step) + 1;

    for (let i = 0; i < stepCount; i++) {
      const value = this.min + i * this.step;
      if (value <= this.max) {
        const color = this.stepColors[i % this.stepColors.length];
        const percentage = range > 0 ? ((value - this.min) / range) * 100 : 0;
        steps.push({ value, color, percentage });
      }
    }
    return steps;
  }

  /**
   * Retrieves an array of step lines that fall within the specified minimum and maximum range.
   *
   * @return {Object[]} An array of objects, where each object contains a `value` property representing the step value and a `percentage` property representing the percentage of that step.
   */
  getStepLines(): { value: number, percentage: number }[] {
    const steps = this.getSteps();
    return steps
      .filter(step => step.value >= (this.min+1) && step.value <= (this.max-1))
      .map(step => ({ value: step.value, percentage: step.percentage }));
  }

  /**
   * Updates the track's gradient background based on predefined steps and their respective colors and percentages.
   * The method calculates the percentage position for each step and generates a linear gradient
   * to visually represent the transitions between the steps.
   * If the value range is invalid (e.g., max is less than or equal to min), a default background color is applied.
   *
   * @return {void} This method does not return a value.
   */
  updateTrackGradient(): void {
    if (!this.track) return;

    const range = this.max - this.min;
    if (range <= 0) {
      this.track.nativeElement.style.background = '#dee2e6';
      return;
    }

    // Create fixed gradient based on step colors
    const steps = this.getSteps();
    let gradient = '';
    for (let i = 0; i < steps.length; i++) {
      const { color, percentage } = steps[i];
      if (i === 0) {
        gradient += `${color} ${percentage}%`;
      } else {
        const prevPercentage = steps[i - 1].percentage;
        gradient += `, ${steps[i - 1].color} ${prevPercentage}%, ${color} ${percentage}%`;
      }
    }

    this.track.nativeElement.style.background = `linear-gradient(to right, ${gradient})`;
  }

  /**
   * Handles the `mousedown` event on a thumb element, initiating the drag functionality.
   * Prevents default behavior, assigns the dragging state, and adds listeners for mousemove and mouseup events.
   *
   * @param {number} index - The index of the thumb element being interacted with.
   * @param {MouseEvent} event - The mouse event object containing details about the mousedown action.
   * @return {void} This method does not return any value.
   */
  onMouseDown(index: number, event: MouseEvent): void {
    // if (this.readOnly === true || (Array.isArray(this.readOnly) && this.readOnly[index])) return;
    if (this.isReadOnly(index)) return;
    event.preventDefault();
    this.isDragging = index;
    this.updateThumbPosition(event, index);
    document.addEventListener('mousemove', this.onMouseMove.bind(this));
    document.addEventListener('mouseup', this.onMouseUp.bind(this));
  }

  /**
   * Event handler to manage mouse movement interactions. Updates the position of a draggable thumb element
   * when dragging is in progress and the component is not in read-only mode.
   *
   * @param {MouseEvent} event - The mouse event object containing details of the mouse movement.
   * @return {void} Does not return a value.
   */
  onMouseMove(event: MouseEvent): void {
    // if (this.isDragging !== null && (this.readOnly === false || (Array.isArray(this.readOnly) && !this.readOnly[this.isDragging]))) {
    if (this.isDragging !== null && !this.isReadOnly(this.isDragging)) {
      this.updateThumbPosition(event, this.isDragging);
    }
  }

  /**
   * Handles the "mouseup" event by resetting the dragging state
   * and removing event listeners for "mousemove" and "mouseup".
   *
   * @return {void} This method does not return a value.
   */
  onMouseUp(): void {
    this.isDragging = null;
    document.removeEventListener('mousemove', this.onMouseMove.bind(this));
    document.removeEventListener('mouseup', this.onMouseUp.bind(this));
  }

  /**
   * Updates the position of a thumb in a slider control based on the mouse event and specified index.
   *
   * @param {MouseEvent} event - The mouse event containing the cursor position when the thumb is moved.
   * @param {number} index - The index of the thumb to update within the slider.
   * @return {void} This method does not return a value.
   */
  updateThumbPosition(event: MouseEvent, index: number): void {
    if (!this.track) return;

    const trackRect = this.track.nativeElement.getBoundingClientRect();
    const relativeX = event.clientX - trackRect.left;
    const percentage = Math.max(0, Math.min(1, relativeX / trackRect.width));

    // Calculate new value, respecting step and bounds
    let newValue = this.min + percentage * (this.max - this.min);
    newValue = Math.round(newValue / this.step) * this.step;
    newValue = Math.max(this.min, Math.min(this.max, newValue));

    // Update value independently
    const newValues = [...this.values];
    newValues[index] = newValue;
    this.values = newValues;
    this.valuesChange.emit(this.values);
    this.changed.emit(this.values);
  }

  /**
   * Calculates the thumb position on a slider based on its index, considering overlapping thumbs and their respective offsets.
   *
   * @param {number} index - The index of the thumb whose position is to be calculated.
   * @return {string} The calculated position of the thumb as a CSS expression, including offset adjustments for overlapping thumbs.
   */
  getThumbPosition(index: number): string {
    const range = this.max - this.min;
    const percentage = range > 0 ? ((this.values[index] - this.min) / range) * 100 : 0;

    // Calculate X-offset for thumbs at the same value
    const sameValueIndices: number[] = this.values
      .map((value, i) => (value === this.values[index] ? i : -1))
      .filter(i => i !== -1);
    const positionInGroup = sameValueIndices.indexOf(index);
    const groupSize = sameValueIndices.length;

    let offsetPx = 0;
    if (groupSize > 1) {
      // Spread thumbs evenly: e.g., [-10, 0, +10] for 3 thumbs
      const spread = Math.floor(groupSize / 2);
      offsetPx = (positionInGroup - spread) * this.OFFSET_PX;
      if (groupSize % 2 === 0 && positionInGroup >= spread) {
        offsetPx += this.OFFSET_PX / 2; // Adjust for even groups
      }
    }

    // Base position minus half thumb width (30px / 2) plus offset
    return `calc(${percentage}% - 15px + ${offsetPx}px)`;
  }

  getStepPosition(percentage: number): string {
    return `calc(${percentage}% - 5px)`; // Adjust for marker width (10px / 2)
  }

  getStepLinePosition(percentage: number): string {
    return `calc(${percentage}% - 0.5px)`; // Adjust for line width (1px / 2)
  }

  getThumbImage(index: number): string {
    return this.thumbImages[index] || '';
  }

  isReadOnly(index: number): boolean {
    return this.readOnly === true || (Array.isArray(this.readOnly) && this.readOnly[index]);
  }
}
